import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import {
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { toast } from 'react-hot-toast';
import { User, X } from 'lucide-react';

// How many lawyer entries to show inline inside an announcement card before
// collapsing the rest behind a "View all" dialog.
const INLINE_LAWYER_PREVIEW = 3;

function StudentUpdates() {
  const { globalUser } = useAuth();
  const navigate = useNavigate();

  const [userCategory, setUserCategory] = useState('');
  const [isChecking, setIsChecking] = useState(true);
  const [updates, setUpdates] = useState([]);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);

  const formatDate = (value) => {
    if (!value) return '';
    const date = value.toDate ? value.toDate() : new Date(value);
    return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const loadData = useCallback(async () => {
    if (!globalUser) return;
    try {
      setIsChecking(true);

      // Confirm the user is a law student.
      const userDoc = await getDoc(doc(db, 'lawyers', globalUser.uid));
      const category = userDoc.exists()
        ? userDoc.data().userCategory || userDoc.data().userCateogry || ''
        : '';
      setUserCategory(category);
      if (category !== 'Law student') return;

      // Fetch active announcements. Posts may be global (no targetUserId) or
      // targeted to a single student (e.g. resume-shared notifications created
      // by the admin when sharing a resume with lawyers).
      let posts = [];
      try {
        const snap = await getDocs(
          query(
            collection(db, 'studentUpdates'),
            where('active', '==', true),
            orderBy('postedAt', 'desc'),
          ),
        );
        posts = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      } catch (err) {
        // Fallback if the composite index isn't set up yet.
        console.warn('studentUpdates query fell back:', err.message);
        const snap = await getDocs(collection(db, 'studentUpdates'));
        posts = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((p) => p.active !== false);
      }

      // This feed shows announcements only — jobs and internships are no longer
      // surfaced here. Also keep only posts addressed to everyone or to this
      // specific student.
      const visible = posts.filter((p) => {
        const isAnnouncement = !p.type || p.type === 'announcement';
        const forThisUser = !p.targetUserId || p.targetUserId === globalUser.uid;
        return isAnnouncement && forThisUser;
      });
      setUpdates(visible);
    } catch (error) {
      console.error('Error loading student updates:', error);
      toast.error('Could not load updates. Please try again.');
    } finally {
      setIsChecking(false);
    }
  }, [globalUser]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ----- Loading -----
  if (isChecking) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-[#050125] flex items-center justify-center">
          <div className="text-white text-xl">Loading...</div>
        </div>
        <Footer />
      </>
    );
  }

  // ----- Restricted (non law-students) -----
  if (userCategory !== 'Law student') {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-[#050125] flex items-center justify-center px-4">
          <div className="bg-[#1E1E45] rounded-2xl p-8 border-2 border-white max-w-md text-center">
            <h2 className="text-2xl font-bold text-white mb-4">Access Restricted</h2>
            <p className="text-white mb-6">Updates are only available for Law Students.</p>
            <button
              onClick={() => navigate('/portal/Dashboard')}
              className="bg-[#C99F4A] hover:bg-[#D19B15] text-white px-6 py-3 rounded-lg font-semibold transition"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-[#050125] py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-[#C99F4A] text-center mb-2">Updates</h1>
          <p className="text-gray-300 text-center mb-8">
            Announcements for law students.
          </p>

          {updates.length === 0 ? (
            <div className="bg-[#1E1E45] rounded-2xl p-10 text-center border border-gray-700">
              <p className="text-gray-300 text-lg">No announcements right now.</p>
              <p className="text-gray-500 text-sm mt-1">
                Check back soon — new announcements are posted here.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {updates.map((update) => {
                const lawyers = Array.isArray(update.sharedLawyers) ? update.sharedLawyers : [];
                const hasOverflow = lawyers.length > INLINE_LAWYER_PREVIEW;
                return (
                  <div
                    key={update.id}
                    className="bg-[#1E1E45] rounded-2xl p-6 border border-gray-700"
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <span className="px-3 py-1 rounded-full text-xs font-semibold border bg-emerald-500/20 text-emerald-300 border-emerald-400/40">
                        Announcement
                      </span>
                      {update.postedAt && (
                        <span className="text-gray-500 text-xs whitespace-nowrap">
                          {formatDate(update.postedAt)}
                        </span>
                      )}
                    </div>
                    <h3 className="text-xl font-bold text-white">{update.title}</h3>
                    {update.description && (
                      <p className="text-gray-300 text-sm mt-3 whitespace-pre-line">
                        {update.description}
                      </p>
                    )}

                    {/* Referral announcements carry a structured list of the
                        lawyers the student's resume was shared with. */}
                    {lawyers.length > 0 && (
                      <div className="mt-4 space-y-2">
                        {lawyers.slice(0, INLINE_LAWYER_PREVIEW).map((l, i) => (
                          <LawyerItem key={l.uid || `${l.name}-${i}`} lawyer={l} />
                        ))}
                        {hasOverflow && (
                          <button
                            onClick={() => setSelectedAnnouncement(update)}
                            className="text-[#C99F4A] hover:text-[#D19B15] text-sm font-semibold mt-1"
                          >
                            View all {lawyers.length} lawyers →
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <Footer />

      {selectedAnnouncement && (
        <AnnouncementLawyersDialog
          announcement={selectedAnnouncement}
          formatDate={formatDate}
          onClose={() => setSelectedAnnouncement(null)}
        />
      )}
    </>
  );
}

// One row in the lawyer list — avatar circle, name and email.
function LawyerItem({ lawyer }) {
  return (
    <div className="flex items-center gap-3 bg-[#2A2A55] rounded-lg p-3 border border-gray-700">
      <div className="w-9 h-9 rounded-full bg-[#C99F4A]/20 flex items-center justify-center shrink-0">
        <User className="w-5 h-5 text-[#C99F4A]" />
      </div>
      <div className="min-w-0">
        <p className="text-white text-sm font-medium truncate">{lawyer.name || 'Lawyer'}</p>
        {lawyer.email && (
          <p className="text-gray-400 text-xs truncate">{lawyer.email}</p>
        )}
      </div>
    </div>
  );
}

// Modal that shows the full announcement text plus every lawyer the resume was
// shared with — used when the inline preview is collapsed.
function AnnouncementLawyersDialog({ announcement, formatDate, onClose }) {
  const lawyers = Array.isArray(announcement.sharedLawyers) ? announcement.sharedLawyers : [];
  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[1100] p-4">
      <div className="bg-[#1E1E45] rounded-2xl border border-gray-700 w-full max-w-lg max-h-[85vh] flex flex-col">
        <div className="flex items-start justify-between gap-3 p-6 border-b border-gray-700">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <span className="px-3 py-1 rounded-full text-xs font-semibold border bg-emerald-500/20 text-emerald-300 border-emerald-400/40">
                Announcement
              </span>
              {announcement.postedAt && (
                <span className="text-gray-500 text-xs">{formatDate(announcement.postedAt)}</span>
              )}
            </div>
            <h2 className="text-xl font-bold text-white">{announcement.title}</h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="p-1.5 rounded-full text-gray-400 hover:text-white hover:bg-white/10 transition-colors shrink-0"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {announcement.description && (
            <p className="text-gray-300 text-sm whitespace-pre-line mb-5">
              {announcement.description}
            </p>
          )}
          {lawyers.length > 0 && (
            <>
              <p className="text-gray-500 text-xs uppercase tracking-wide mb-2">
                Shared with {lawyers.length} lawyer{lawyers.length > 1 ? 's' : ''}
              </p>
              <div className="space-y-2">
                {lawyers.map((l, i) => (
                  <LawyerItem key={l.uid || `${l.name}-${i}`} lawyer={l} />
                ))}
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end px-6 py-4 border-t border-gray-700">
          <button
            onClick={onClose}
            className="bg-[#C99F4A] hover:bg-[#D19B15] text-black px-5 py-2 rounded-lg text-sm font-semibold transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default StudentUpdates;
